import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BoltIcon from '@mui/icons-material/Bolt';
import HubIcon from '@mui/icons-material/Hub';
import QuizIcon from '@mui/icons-material/Quiz';
import ForumIcon from '@mui/icons-material/Forum';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import TuneIcon from '@mui/icons-material/Tune';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const TABS = [
  { key: 'intents', label: 'Intents', icon: <BoltIcon fontSize="small" /> },
  { key: 'entities', label: 'Entities', icon: <HubIcon fontSize="small" /> },
  { key: 'faqs', label: 'FAQs', icon: <QuizIcon fontSize="small" /> },
  { key: 'synonyms', label: 'Synonyms', icon: <SwapHorizIcon fontSize="small" /> },
  { key: 'smalltalk', label: 'Small Talk', icon: <ForumIcon fontSize="small" /> },
  { key: 'context', label: 'Context', icon: <PsychologyAltIcon fontSize="small" /> },
  { key: 'configuration', label: 'Configuration', icon: <TuneIcon fontSize="small" /> },
  { key: 'test-nlu', label: 'Test NLU', icon: <AutoAwesomeIcon fontSize="small" /> },
  { key: 'training', label: 'Training', icon: <PsychologyAltIcon fontSize="small" /> },
  { key: 'models', label: 'Model Versions', icon: <HubIcon fontSize="small" /> },
  { key: 'analytics', label: 'Analytics', icon: <BoltIcon fontSize="small" /> },
];

/** Purpose: convert newline-separated editor text into clean entries. Why: training phrases and responses are stored as arrays by the API. */
const splitLines = (value = '') =>
  String(value || '')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

/** Purpose: render stored entries in the multiline editor format. Why: editors need a reversible representation of the API arrays. */
const joinLines = (items = []) => (Array.isArray(items) ? items.join('\n') : '');

/** Purpose: merge saved NLU settings with supported defaults and bounds. Why: older environments may omit newer threshold fields. */
const getNluThresholdSettings = (config = {}) => ({
  nluMode: ['legacy', 'hybrid'].includes(config.settings?.nluMode) ? config.settings.nluMode : 'legacy',
  intentConfidenceThreshold: Number.isFinite(Number(config.settings?.intentConfidenceThreshold))
    ? Math.min(100, Math.max(0, Math.round(Number(config.settings.intentConfidenceThreshold))))
    : 56,
  intentIgnoreThreshold: Number.isFinite(Number(config.settings?.intentIgnoreThreshold))
    ? Math.min(100, Math.max(0, Math.round(Number(config.settings.intentIgnoreThreshold))))
    : 30,
  ambiguityMargin: Number.isFinite(Number(config.settings?.ambiguityMargin)) ? Number(config.settings.ambiguityMargin) : 0.08,
  outOfScopeThreshold: Number.isFinite(Number(config.settings?.outOfScopeThreshold)) ? Number(config.settings.outOfScopeThreshold) : 0.45,
  nluQualityGate: {
    enabled: Boolean(config.settings?.nluQualityGate?.enabled),
    requireEvaluation: config.settings?.nluQualityGate?.requireEvaluation !== false,
    minimumAccuracy: Number(config.settings?.nluQualityGate?.minimumAccuracy ?? 0.8),
    minimumMacroF1: Number(config.settings?.nluQualityGate?.minimumMacroF1 ?? 0.75),
    maximumFallbackRate: Number(config.settings?.nluQualityGate?.maximumFallbackRate ?? 0.25),
    maximumAmbiguousRate: Number(config.settings?.nluQualityGate?.maximumAmbiguousRate ?? 0.2),
  },
});

/** Purpose: provide page-level training layout styles. Why: centralizes consistent spacing and control sizing across tabs. */
const getShellSx = (theme) => ({
  p: 0,
  minHeight: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  '& .MuiButton-root': { borderRadius: '8px', boxShadow: 'none', textTransform: 'none', whiteSpace: 'nowrap', fontSize: 13 },
  '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 14 },
  '& .MuiInputLabel-root': { fontSize: 14 },
  '& .MuiChip-root': { borderRadius: '6px', fontSize: 12, height: 26 },
  '& .MuiAlert-root': { borderRadius: '10px' },
  '& .MuiTypography-h6': { fontSize: 17 },
});

/** Purpose: define the shared surface style for training cards. Why: keeps the many editor panels visually consistent in both themes. */
const getCardSx = (theme) => ({
  borderRadius: '12px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.16)' : 'rgba(148,163,184,0.18)'}`,
  background: theme.palette.background.paper,
  boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
});

/** Purpose: define a subdued nested panel surface. Why: visually distinguishes supporting metrics from primary controls. */
const getMutedPanelSx = (theme) => ({
  borderRadius: '10px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.2)'}`,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.62)' : 'rgba(255,255,255,0.86)',
});

/** Purpose: apply shared theme-aware field styling. Why: editing controls should have consistent contrast and focus feedback. */
const getTextFieldSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.72)' : 'rgba(255,255,255,0.96)',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.28)',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(96,165,250,0.42)' : 'rgba(37,99,235,0.42)',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: theme.palette.mode === 'dark' ? '0 0 0 3px rgba(59,130,246,0.16)' : '0 0 0 3px rgba(59,130,246,0.12)',
    },
  },
});

/** Purpose: display one compact NLU metric. Why: standardizes overview cards without repeating layout markup. */
function StatsCard({ label, value, helper, icon }) {
  return (
    <Paper
      sx={(theme) => ({
        ...getCardSx(theme),
        p: 1.75,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      })}
    >
      <Box sx={{ position: 'relative' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        </Stack>
        <Typography sx={{ mt: 0.5, fontSize: 28, fontWeight: 750, lineHeight: 1.2, letterSpacing: '-0.04em' }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', lineHeight: 1.4 }}>
          {helper}
        </Typography>
      </Box>
    </Paper>
  );
}

/** Purpose: render searchable records beside their editor/detail pane. Why: intents and related NLU resources share the same browsing pattern. */
function SplitLayout({ title, subtitle, searchValue, onSearchChange, list, selectedId, onSelect, renderLabel, renderMeta, actions, detail, error }) {
  /** Purpose: Perform filtered for the train module. Why: Callers need a consistent result from this shared operation. */
  const filtered = useMemo(() => {
    const query = String(searchValue || '').toLowerCase();
    if (!query) return list;
    return list.filter((item) => renderLabel(item).toLowerCase().includes(query));
  }, [list, renderLabel, searchValue]);

  return (
    <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '260px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
        <Box sx={{ minWidth: 0, position: { md: 'sticky' }, top: 88 }}>
          <Paper sx={(theme) => ({ ...getCardSx(theme), p: 2 })}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {title} <Box component="span" sx={{ color: 'text.secondary', fontSize: 13, ml: 0.5 }}>{list.length}</Box>
                </Typography>
              </Box>
              {actions}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{subtitle}</Typography>
            <TextField
              fullWidth
              size="small"
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={`Search ${title.toLowerCase()}`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={(theme) => ({ ...getTextFieldSx(theme), mb: 2 })}
            />
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}
            <List sx={{ maxHeight: { xs: 240, md: '55vh' }, overflowY: 'auto', p: 0 }}>
              {filtered.map((item) => {
                const id = item.intentId || item.entityId || item.faqId || item.synonymId || item.smallTalkId || item.contextId || item.sessionKey;
                return (
                  <ListItemButton
                    key={id}
                    selected={selectedId === id}
                    onClick={() => onSelect(item)}
                    sx={{
                      mb: 1,
                      borderRadius: '8px',
                      alignItems: 'flex-start',
                      border: '1px solid',
                      borderColor: selectedId === id ? 'primary.main' : 'transparent',
                      backgroundColor: selectedId === id ? 'action.selected' : 'transparent',
                      px: 1.5,
                      py: 0.75,
                      transition: 'all 0.18s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <ListItemText
                      primary={renderLabel(item)}
                      secondary={renderMeta(item)}
                      primaryTypographyProps={{ fontWeight: 650, fontSize: 14, sx: { overflowWrap: 'anywhere' } }}
                      secondaryTypographyProps={{ sx: { mt: 0.5, color: 'text.secondary', fontSize: 12 } }}
                    />
                  </ListItemButton>
                );
              })}
              {filtered.length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
                  No items found yet.
                </Typography>
              )}
            </List>
          </Paper>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {detail}
        </Box>
    </Box>
  );
}

/** Purpose: frame a selected record's editable details. Why: provides a common header/action structure for editor panels. */
function DetailCard({ title, subtitle, children, actions }) {
  return (
    <Paper sx={(theme) => ({ ...getCardSx(theme), p: { xs: 2, md: 2.5 } })}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {actions}
      </Stack>
      {children}
    </Paper>
  );
}

/** Purpose: explain when no record is selected. Why: keeps the detail pane intentional instead of appearing broken or blank. */
function EmptyDetail({ title, subtitle }) {
  return (
    <Paper sx={(theme) => ({ ...getCardSx(theme), p: 4, minHeight: 420, display: 'grid', placeItems: 'center', textAlign: 'center' })}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );
}

/** Purpose: edit an intent and its training/routing configuration. Why: groups intent-specific fields and actions in one controlled editor. */
function IntentEditor({ entityOptions, flowOptions, value, onChange, onSave, onDelete, saving }) {
  return (
    <DetailCard
      title={value?.intentId ? value.name || 'Intent details' : 'New intent'}
      subtitle="Define utterances, bot responses, linked entities, and connected flows."
      actions={
        <Stack direction="row" spacing={1}>
          {value?.intentId ? (
            <Button color="error" onClick={() => onDelete(value)} disabled={saving}>
              Delete
            </Button>
          ) : null}
          <Button variant="contained" onClick={() => onSave(value)} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save intent'}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Intent name" value={value.name} onChange={(event) => onChange('name', event.target.value)} sx={(theme) => getTextFieldSx(theme)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Description" value={value.description} onChange={(event) => onChange('description', event.target.value)} sx={(theme) => getTextFieldSx(theme)} />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            maxRows={10}
            label="Training phrases"
            helperText={`${splitLines(value.trainingPhrasesText).length} phrases · One phrase per line`}
            value={value.trainingPhrasesText}
            onChange={(event) => onChange('trainingPhrasesText', event.target.value)}
            sx={(theme) => getTextFieldSx(theme)}
          />
          {value.suggestedPhrases?.length ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
              {value.suggestedPhrases.map((phrase) => (
                <Chip
                  key={phrase}
                  size="small"
                  label={phrase}
                  onClick={() => {
                    const current = splitLines(value.trainingPhrasesText);
                    if (!current.includes(phrase)) {
                      onChange('trainingPhrasesText', joinLines([...current, phrase]));
                    }
                  }}
                  variant="outlined"
                />
              ))}
            </Stack>
          ) : null}
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            label="Responses"
            helperText="One response per line"
            value={value.responsesText}
            onChange={(event) => onChange('responsesText', event.target.value)}
            sx={(theme) => getTextFieldSx(theme)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            SelectProps={{
              multiple: true,
              /** Purpose: Perform render value for the train module. Why: Callers need a consistent result from this shared operation. */
              renderValue: (selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(Array.isArray(selected) ? selected : []).map((name) => (
                    <Chip key={name} size="small" label={name} />
                  ))}
                </Box>
              ),
            }}
            label="Linked entities"
            helperText="Entities to extract with this intent"
            value={value.entities || []}
            onChange={(event) => {
              const val = event.target.value;
              onChange('entities', typeof val === 'string' ? val.split(',') : val);
            }}
            sx={(theme) => getTextFieldSx(theme)}
          >
            {entityOptions.length === 0 ? (
              <MenuItem disabled value="">
                <em>No entities created yet</em>
              </MenuItem>
            ) : (
              entityOptions.map((entity) => (
                <MenuItem key={entity.entityId} value={entity.name}>
                  {entity.name}
                </MenuItem>
              ))
            )}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Connected flow"
            helperText="Switch to this flow journey when intent is detected"
            value={
              Array.isArray(value.flowsConnected)
                ? (value.flowsConnected[0] || '')
                : (value.flowsConnected || '')
            }
            onChange={(event) => {
              const selectedFlow = event.target.value;
              onChange('flowsConnected', selectedFlow ? [selectedFlow] : []);
            }}
            sx={(theme) => getTextFieldSx(theme)}
          >
            <MenuItem value="">
              <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                None (No connected flow)
              </Box>
            </MenuItem>
            {flowOptions.map((flow) => (
              <MenuItem key={flow.flowId} value={flow.flowId}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <span>{flow.name || flow.flowId}</span>
                  {flow.isDefault ? (
                    <Chip size="small" label="Default" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', ml: 1 }} />
                  ) : null}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </DetailCard>
  );
}

/** Purpose: edit a resource described by field metadata. Why: FAQs, synonyms, and similar records can reuse one validated form layout. */
function GenericEditor({ title, subtitle, value, onChange, onSave, onDelete, saving, fields }) {
  return (
    <DetailCard
      title={title}
      subtitle={subtitle}
      actions={
        <Stack direction="row" spacing={1}>
          {value?.id ? (
            <Button color="error" onClick={() => onDelete(value)} disabled={saving}>
              Delete
            </Button>
          ) : null}
          <Button variant="contained" onClick={() => onSave(value)} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2}>
        {fields.map((field) => (
          <Grid item xs={12} md={field.md || 12} key={field.key}>
            <TextField
              fullWidth
              select={Boolean(field.options)}
              SelectProps={field.multiple ? { multiple: true } : undefined}
              multiline={field.multiline}
              minRows={field.minRows}
              label={field.label}
            helperText={field.helperText}
            value={value[field.key] ?? (field.multiple ? [] : '')}
            onChange={(event) => onChange(field.key, event.target.value)}
            sx={(theme) => getTextFieldSx(theme)}
            >
              {(field.options || []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ))}
      </Grid>
    </DetailCard>
  );
}

/** Purpose: show NLU analysis input and parse results. Why: makes predicted intent, confidence, entities, and routing diagnostics inspectable. */
function AnalyzePanel({ message, onChange, onRun, result, running }) {
  const displayedIntentConfidence =
    result?.intent?.confidence ?? result?.predictions?.[0]?.confidence ?? result?.confidence ?? 0;

  return (
    <Paper sx={(theme) => ({ ...getCardSx(theme), p: 2.5, overflow: 'hidden' })}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
        <Box sx={{ minWidth: { lg: 180 } }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2 }}>
            Live Playground
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800 }}>
            Test live NLU
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
            Validate intents, entities, FAQs, and context before going live.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Test message"
              placeholder="Type a user message to test synonyms, intent, entities, FAQ and context resolution"
              value={message}
              onChange={(event) => onChange(event.target.value)}
              sx={(theme) => getTextFieldSx(theme)}
            />
            <Button variant="contained" size="large" startIcon={<AutoAwesomeIcon />} onClick={onRun} disabled={running} sx={{ minWidth: 148, height: 52, borderRadius: 2.5 }}>
              {running ? <CircularProgress size={18} /> : 'Analyze'}
            </Button>
          </Stack>
        </Box>
      </Stack>

      {result ? (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={(theme) => ({ ...getMutedPanelSx(theme), p: 2 })}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>Intent</Typography>
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 700 }}>
                {result.intent?.name || 'No strong intent'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Confidence: {displayedIntentConfidence}%
              </Typography>
              {!result.intent && Number.isFinite(Number(result.intentThreshold)) && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                  Trigger: {result.intentThreshold}% · Ignore below: {Number.isFinite(Number(result.intentIgnoreThreshold)) ? result.intentIgnoreThreshold : 30}%
                </Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={(theme) => ({ ...getMutedPanelSx(theme), p: 2 })}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>Entities</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                {(result.entities || []).map((entity) => (
                  <Chip key={`${entity.entity}-${entity.value}`} label={`${entity.entity}: ${entity.value}`} />
                ))}
                {(!result.entities || result.entities.length === 0) && <Chip label="No entities" variant="outlined" />}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={(theme) => ({ ...getMutedPanelSx(theme), p: 2 })}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>Resolution</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {result.smallTalk?.response || result.faq?.answer || 'Flow intent / fallback path'}
              </Typography>
              {result.slotFilling?.length ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                  {result.slotFilling.map((slot) => (
                    <Chip key={slot} size="small" color="warning" label={`Need ${slot}`} />
                  ))}
                </Stack>
              ) : null}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={(theme) => ({ ...getMutedPanelSx(theme), p: 2 })}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>Top predictions</Typography>
              <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                {(result.predictions || []).slice(0, 5).map((prediction) => (
                  <Stack key={prediction.intentId || prediction.name} direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ minWidth: 140 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {prediction.name}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, height: 10, borderRadius: 999, bgcolor: 'action.hover', overflow: 'hidden' }}>
                      <Box
                        sx={{
                          width: `${Math.max(4, prediction.confidence || 0)}%`,
                          height: '100%',
                          borderRadius: 999,
                          bgcolor: prediction.confidence >= 60 ? 'success.main' : prediction.confidence >= 40 ? 'warning.main' : 'primary.main',
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'right', fontWeight: 700 }}>
                      {prediction.confidence || 0}%
                    </Typography>
                  </Stack>
                ))}
                {(!result.predictions || result.predictions.length === 0) ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Add more training phrases to improve intent scoring.
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      ) : null}
    </Paper>
  );
}

/** Purpose: test entity extraction against sample text. Why: lets authors verify entity patterns before relying on them in live flows. */
function EntityTestPanel({ input, onChange, onRun, running, result }) {
  return (
    <Paper sx={(theme) => ({ ...getCardSx(theme), p: 2.5, mb: 2.5 })}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
        <TextField
          fullWidth
          label="Entity extraction tester"
          placeholder="Try: book flight from Delhi tomorrow for 2 people"
          value={input}
          onChange={(event) => onChange(event.target.value)}
          sx={(theme) => getTextFieldSx(theme)}
        />
        <Button variant="contained" onClick={onRun} disabled={running}>
          {running ? <CircularProgress size={18} /> : 'Extract'}
        </Button>
      </Stack>
      {result ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {result.error ? <Alert severity="error">{result.error}</Alert> : null}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Normalized: {result.normalizedMessage || input}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {(result.entities || []).map((entity) => (
              <Chip key={`${entity.entity}-${entity.value}`} label={`${entity.entity}: ${entity.value}`} />
            ))}
            {(!result.entities || result.entities.length === 0) && <Chip variant="outlined" label="No entities found" />}
          </Stack>
        </Stack>
      ) : null}
    </Paper>
  );
}

/** Purpose: edit environment-specific NLU thresholds and quality gates. Why: routing behavior must be tunable without changing the training data. */
function ConfigurationTab({ botId, envName, environmentConfig, onConfigSaved }) {
  /** Purpose: Load initial settings for the train module. Why: The selected view needs current data to render or continue processing. */
  const initialSettings = useMemo(
    () => getNluThresholdSettings(environmentConfig?.config || {}),
    [environmentConfig]
  );
  const [settings, setSettings] = useState(initialSettings);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setSettings(initialSettings);
    setMessage(null);
  }, [initialSettings]);

  // Keep form values local until Save so threshold edits remain explicit and reversible.
  /** Purpose: Apply set threshold in the train module. Why: Centralizing this change keeps related state or persisted data consistent. */
  const setThreshold = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  // Clamp and normalize numeric thresholds when an editor loses focus.
  /** Purpose: Provide the normalize threshold operation for the train module. Why: Callers depend on this shared result to keep the surrounding flow consistent. */
  const normalizeThreshold = (key) => {
    setSettings((current) => ({
      ...current,
      [key]: Math.min(100, Math.max(0, Math.round(Number(current[key]) || 0))),
    }));
  };

  const triggerThreshold = Number(settings.intentConfidenceThreshold);
  const ignoreThreshold = Number(settings.intentIgnoreThreshold);
  const ambiguityMargin = Number(settings.ambiguityMargin);
  const outOfScopeThreshold = Number(settings.outOfScopeThreshold);
  const gateRates = settings.nluQualityGate ? ['minimumAccuracy', 'minimumMacroF1', 'maximumFallbackRate', 'maximumAmbiguousRate'].map((key) => Number(settings.nluQualityGate[key])) : [];
  const hasInvalidThreshold =
    !Number.isFinite(triggerThreshold) ||
    !Number.isFinite(ignoreThreshold) ||
    triggerThreshold < 0 ||
    triggerThreshold > 100 ||
    ignoreThreshold < 0 ||
    ignoreThreshold > 100 ||
    ignoreThreshold > triggerThreshold ||
    !Number.isFinite(ambiguityMargin) || ambiguityMargin < 0 || ambiguityMargin > 1 ||
    !Number.isFinite(outOfScopeThreshold) || outOfScopeThreshold < 0 || outOfScopeThreshold > 1 ||
    (settings.nluQualityGate?.enabled && gateRates.some((value) => !Number.isFinite(value) || value < 0 || value > 1));

  // Persist validated NLU settings to the selected environment, then notify the parent to refresh its snapshot.
  /** Purpose: Handle handle save in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handleSave = async () => {
    if (!botId || !envName) return;
    if (hasInvalidThreshold) {
      setMessage({ type: 'error', text: 'Ignore below must be between 0 and trigger threshold. Both values should be 0–100%.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const response = await axios.put(`/api/v1/environments/${botId}/${envName}`, {
        config: {
          settings: {
            ...(environmentConfig?.config?.settings || {}),
            intentConfidenceThreshold: Math.round(triggerThreshold),
            intentIgnoreThreshold: Math.round(ignoreThreshold),
            nluMode: settings.nluMode,
            ambiguityMargin: Number(settings.ambiguityMargin),
            outOfScopeThreshold: Number(settings.outOfScopeThreshold),
            nluQualityGate: settings.nluQualityGate,
          },
        },
      });
      onConfigSaved(response.data.environment);
      setMessage({ type: 'success', text: 'NLU configuration saved.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save NLU configuration.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={(theme) => ({ ...getCardSx(theme), p: { xs: 2.5, md: 3 } })}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 2 }}>
            NLU CONFIGURATION
          </Typography>
          <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800 }}>
            Intent routing control
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary', maxWidth: 720 }}>
            Confidence ke basis par decide karo kab dusra flow trigger hoga aur kab weak prediction ignore hoga.
          </Typography>
        </Box>

        {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}><TextField fullWidth select label="NLU engine" value={settings.nluMode} onChange={(e) => setThreshold('nluMode', e.target.value)}><MenuItem value="legacy">Legacy fuzzy</MenuItem><MenuItem value="hybrid">Zelorich Hybrid</MenuItem></TextField></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Ambiguity margin" value={settings.ambiguityMargin} onChange={(e) => setThreshold('ambiguityMargin', e.target.value)} inputProps={{ min: 0, max: 1, step: .01 }} helperText="Top-1 minus Top-2 required to auto-route" /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Out-of-scope threshold" value={settings.outOfScopeThreshold} onChange={(e) => setThreshold('outOfScopeThreshold', e.target.value)} inputProps={{ min: 0, max: 1, step: .01 }} helperText="Below this score, return out of scope" /></Grid>
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={(theme) => ({
                ...getMutedPanelSx(theme),
                p: 2.5,
                height: '100%',
                borderColor: 'primary.main',
              })}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Flow trigger confidence
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75, mb: 2 }}>
                Prediction itna ya zyada confident ho tabhi linked journey/flow switch hoga.
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Trigger flow at"
                value={settings.intentConfidenceThreshold}
                onChange={(event) => setThreshold('intentConfidenceThreshold', event.target.value)}
                onBlur={() => normalizeThreshold('intentConfidenceThreshold')}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                inputProps={{ min: 0, max: 100, step: 1 }}
                error={hasInvalidThreshold}
                sx={(theme) => getTextFieldSx(theme)}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={(theme) => ({ ...getMutedPanelSx(theme), p: 2.5, height: '100%' })}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Ignore weak predictions
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75, mb: 2 }}>
                Isse neeche wala intent weak maana jayega; current flow disturb nahi hoga.
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Ignore below"
                value={settings.intentIgnoreThreshold}
                onChange={(event) => setThreshold('intentIgnoreThreshold', event.target.value)}
                onBlur={() => normalizeThreshold('intentIgnoreThreshold')}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                inputProps={{ min: 0, max: 100, step: 1 }}
                error={hasInvalidThreshold}
                sx={(theme) => getTextFieldSx(theme)}
              />
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={(theme) => ({ ...getMutedPanelSx(theme), p: 2, borderStyle: 'dashed' })}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
            <Chip color="success" label={`≥ ${Math.round(triggerThreshold || 0)}%: flow trigger`} />
            <Chip color="warning" label={`${Math.round(ignoreThreshold || 0)}–${Math.round(triggerThreshold || 0)}%: prediction only`} />
            <Chip color="default" variant="outlined" label={`< ${Math.round(ignoreThreshold || 0)}%: ignore`} />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={(theme) => ({ ...getMutedPanelSx(theme), p: 2.5 })}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
              <Box><Typography fontWeight={800}>Publish quality gate</Typography><Typography variant="body2" color="text.secondary">Candidate metrics pass hone ke baad hi model publish hoga. Existing bots ke liye default off hai.</Typography></Box>
              <Button variant={settings.nluQualityGate.enabled ? 'contained' : 'outlined'} onClick={() => setSettings((current) => ({ ...current, nluQualityGate: { ...current.nluQualityGate, enabled: !current.nluQualityGate.enabled } }))}>{settings.nluQualityGate.enabled ? 'Enabled' : 'Enable gate'}</Button>
            </Stack>
            {settings.nluQualityGate.enabled ? <Grid container spacing={2}>{[['minimumAccuracy','Minimum accuracy'],['minimumMacroF1','Minimum Macro F1'],['maximumFallbackRate','Maximum fallback rate'],['maximumAmbiguousRate','Maximum ambiguous rate']].map(([key,label]) => <Grid item xs={12} sm={6} key={key}><TextField fullWidth type="number" label={label} value={settings.nluQualityGate[key]} inputProps={{ min: 0, max: 1, step: .01 }} onChange={(event) => setSettings((current) => ({ ...current, nluQualityGate: { ...current.nluQualityGate, [key]: Number(event.target.value) } }))} /></Grid>)}</Grid> : null}
          </Stack>
        </Paper>

        <Stack direction="row" justifyContent="flex-end">
          <Button variant="contained" onClick={handleSave} disabled={saving || hasInvalidThreshold} sx={{ borderRadius: 2.5, px: 3, minWidth: 160 }}>
            {saving ? <CircularProgress size={18} /> : 'Save configuration'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/** Purpose: manage the bot's knowledge, training, and NLU tools. Why: operators need one place to curate content and evaluate how the bot understands user input. */
/** Purpose: coordinate the NLU training workspace and its tab data. Why: this page is the shared entry point for authoring, testing, training, and monitoring models. */
export default function Train() {
  const navigate = useNavigate();
  const { selectedBot, selectedEnv } = useAuth();
  const [environmentId, setEnvironmentId] = useState('');
  const [environmentConfig, setEnvironmentConfig] = useState(null);
  const [environmentLoading, setEnvironmentLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [overview, setOverview] = useState(null);
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [lastTrained, setLastTrained] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [training, setTraining] = useState(false);
  const [banner, setBanner] = useState(null);
  const [analysisInput, setAnalysisInput] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [trainingJob, setTrainingJob] = useState(null);
  const [showQuickTest, setShowQuickTest] = useState(false);

	  useEffect(() => {
	    let active = true;
	    if (!selectedBot || !selectedEnv) {
	      setEnvironmentLoading(false);
	      return () => { active = false; };
	    }
	    setEnvironmentLoading(true);
	    axios
	      .get(`/api/v1/environments/${selectedBot}/${selectedEnv}`)
	      .then((response) => {
	        if (!active) return;
	        setEnvironmentId(response.data.environment.envId);
	        setEnvironmentConfig(response.data.environment);
	      })
	      .catch(() => {
	        if (active) setBanner({ type: 'error', text: 'Failed to load the selected environment.' });
	      })
	      .finally(() => { if (active) setEnvironmentLoading(false); });
    return () => { active = false; };
	  }, [selectedBot, selectedEnv]);

  // Load the environment's model/quality summary and current training state together for a coherent header.
  /** Purpose: Load load overview for the train module. Why: The selected view or service needs current scoped data before continuing. */
  const loadOverview = useCallback(async () => {
    if (!selectedBot || !environmentId) return;
    setLoadingOverview(true);
    try {
      const [overviewResponse, statusResponse] = await Promise.all([
        axios.get('/api/v1/train/overview', { params: { botId: selectedBot, environmentId } }),
        axios.get('/api/v1/train/intents/training-status', { params: { botId: selectedBot, environmentId } }),
      ]);
      setOverview(overviewResponse.data.overview);
      setTrainingStatus(statusResponse.data.statistics);
      setLastTrained(statusResponse.data.lastTrained);
      setBanner(null);
    } catch (error) {
      setBanner({ type: 'error', text: error.response?.data?.error || 'Failed to load NLU overview.' });
    } finally {
      setLoadingOverview(false);
    }
  }, [environmentId, selectedBot]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  // Start a training job for the selected bot/environment and surface its returned job state.
  /** Purpose: Handle handle train in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handleTrain = async () => {
    if (!selectedBot || !environmentId) return;
    setTraining(true);
    try {
      const response = await axios.post('/api/v1/nlu/train', {
        botId: selectedBot,
        environment: environmentId,
      });
      setTrainingJob(response.data);
      setTabValue(8);
      setBanner({ type: 'info', text: `Model v${response.data.modelVersion} queued for training.` });
    } catch (error) {
      setBanner({ type: 'error', text: error.response?.data?.error || 'Training failed.' });
    } finally {
      setTraining(false);
    }
  };

  const [publishing, setPublishing] = useState(false);

  // Request promotion of the trained model; production changes remain an explicit operator action.
  /** Purpose: Handle handle publish to prod in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handlePublishToProd = async () => {
    if (!selectedBot) return;
    setPublishing(true);
    try {
      const response = await axios.post('/api/v1/train/publish', { botId: selectedBot });
      setBanner({
        type: 'info',
        text: response.data?.message || 'Publish request submitted! Admin approval is required in the Release Center before changes go live to PROD.',
        action: (
          <Button color="inherit" size="small" onClick={() => navigate('/deployments')}>
            Release Center
          </Button>
        ),
      });
    } catch (error) {
      setBanner({
        type: 'error',
        text: error.response?.data?.error || 'Failed to submit publish request.',
      });
    } finally {
      setPublishing(false);
    }
  };

  // Parse sample text using the selected environment so authors can validate routing before release.
  /** Purpose: Handle handle analyze in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handleAnalyze = async () => {
    if (!analysisInput.trim() || !selectedBot || !environmentId) return;
    setAnalyzing(true);
    try {
      const response = await axios.post('/api/v1/train/analyze', {
        botId: selectedBot,
        environmentId,
        message: analysisInput.trim(),
      });
      setAnalysisResult(response.data.analysis);
      setBanner(null);
    } catch (error) {
      setBanner({ type: 'error', text: error.response?.data?.error || 'Failed to analyze the message.' });
    } finally {
      setAnalyzing(false);
    }
  };

  if (!selectedBot || !selectedEnv) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a bot and environment to access the NLU workspace.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={(theme) => getShellSx(theme)}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography component="h1" sx={{ fontSize: 26, fontWeight: 750, letterSpacing: '-0.035em' }}>Knowledge Lab</Typography>
            <Chip size="small" variant="outlined" sx={{ minWidth: 92, justifyContent: 'center' }} label={environmentConfig ? `${getNluThresholdSettings(environmentConfig.config || {}).nluMode === 'hybrid' ? 'Hybrid' : 'Legacy'} NLU` : 'Loading NLU…'} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>Teach, test and refine how your bot understands customers.</Typography>
          <Typography variant="caption" color="text.secondary">{selectedBot} · {selectedEnv.toUpperCase()} · Last trained: {lastTrained ? new Date(lastTrained).toLocaleString() : 'Never'}</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="outlined" startIcon={<AutoAwesomeIcon />} aria-expanded={showQuickTest} aria-controls="nlu-quick-test" onClick={() => setShowQuickTest((open) => !open)}>{showQuickTest ? 'Close quick test' : 'Quick test'}</Button>
          <Button variant="contained" startIcon={training ? <CircularProgress size={16} color="inherit" /> : <PsychologyAltIcon />} onClick={handleTrain} disabled={training || !environmentId} sx={{ height: 40, px: 2 }}>Train model</Button>
          {selectedEnv === 'dev' && (
            <Button
              variant="contained"
              startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
              onClick={handlePublishToProd}
              disabled={publishing || training}
              sx={{ height: 40, px: 2, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
            >
              Publish to PROD
            </Button>
          )}
        </Stack>
      </Stack>

      <LinearProgress
        aria-label="Loading Knowledge Lab environment"
        sx={{ mb: 2, visibility: environmentLoading || loadingOverview ? 'visible' : 'hidden', borderRadius: 1 }}
      />

      {banner ? (
        <Alert severity={banner.type} action={banner.action} onClose={() => setBanner(null)} sx={{ mb: 2 }}>
          {banner.text}
        </Alert>
      ) : null}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatsCard icon={<BoltIcon fontSize="small" />} label="Intents" value={loadingOverview ? '...' : overview?.totalIntents || 0} helper={`${trainingStatus?.intentsWithUtterances || 0} with training phrases`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatsCard icon={<ForumIcon fontSize="small" />} label="Training phrases" value={loadingOverview ? '...' : trainingStatus?.totalUtterances || 0} helper="Examples across all intents" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatsCard icon={<HubIcon fontSize="small" />} label="Entities / synonyms" value={loadingOverview ? '...' : `${overview?.totalEntities || 0} / ${overview?.totalSynonyms || 0}`} helper="Values and language variants" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatsCard icon={<PsychologyAltIcon fontSize="small" />} label="Live contexts" value={loadingOverview ? '...' : overview?.totalContexts || 0} helper="Active conversation sessions" />
        </Grid>
      </Grid>

      <Collapse in={showQuickTest}>
        <Box id="nlu-quick-test" sx={{ mb: 2 }}>
          <AnalyzePanel message={analysisInput} onChange={setAnalysisInput} onRun={handleAnalyze} result={analysisResult} running={analyzing} />
        </Box>
      </Collapse>

      <Paper component="nav" aria-label="Knowledge Lab sections" sx={(theme) => ({ ...getCardSx(theme), p: 0.5 })}>
        <Tabs
          value={tabValue}
          onChange={(event, next) => setTabValue(next)}
          variant="standard"
          aria-label="NLU workspace sections"
          sx={{
            minHeight: 40,
            '& .MuiTabs-flexContainer': { flexWrap: 'wrap', gap: 0.5 },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTab-root': {
              minHeight: 38,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              color: 'text.secondary',
              minWidth: 0,
              px: 1.25,
            },
            '& .Mui-selected': {
              color: 'primary.main',
              bgcolor: 'action.selected',
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.key} icon={tab.icon} iconPosition="start" label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {tabValue === 0 ? <IntentsTab botId={selectedBot} environmentId={environmentId} onRefreshOverview={loadOverview} /> : null}
        {tabValue === 1 ? <EntitiesTab botId={selectedBot} environmentId={environmentId} onRefreshOverview={loadOverview} /> : null}
        {tabValue === 2 ? <FaqsTab botId={selectedBot} environmentId={environmentId} onRefreshOverview={loadOverview} /> : null}
        {tabValue === 3 ? <SynonymsTab botId={selectedBot} environmentId={environmentId} onRefreshOverview={loadOverview} /> : null}
	        {tabValue === 4 ? <SmallTalkTab botId={selectedBot} environmentId={environmentId} onRefreshOverview={loadOverview} /> : null}
	        {tabValue === 5 ? <ContextTab botId={selectedBot} environmentId={environmentId} onRefreshOverview={loadOverview} /> : null}
	        {tabValue === 6 ? (
	          <ConfigurationTab
	            botId={selectedBot}
	            envName={selectedEnv}
	            environmentConfig={environmentConfig}
	            onConfigSaved={setEnvironmentConfig}
	          />
	        ) : null}
	        {tabValue === 7 ? <HybridTestConsole botId={selectedBot} environmentId={environmentId} /> : null}
	        {tabValue === 8 ? <TrainingPanel botId={selectedBot} environmentId={environmentId} initialJob={trainingJob} onTrain={handleTrain} training={training} /> : null}
	        {tabValue === 9 ? <ModelVersionsPanel botId={selectedBot} environmentId={environmentId} /> : null}
	        {tabValue === 10 ? <NluAnalyticsPanel botId={selectedBot} environmentId={environmentId} /> : null}
	      </Box>
    </Box>
  );
}

/** Purpose: render a concise status label for an NLU decision. Why: test output needs a consistent visual distinction between outcomes. */
function DecisionChip({ status }) {
  const color = status === 'accepted' ? 'success' : status === 'ambiguous' ? 'warning' : status === 'out_of_scope' ? 'error' : 'default';
  return <Chip color={color} label={String(status || 'unknown').replaceAll('_', ' ').toUpperCase()} />;
}

/** Purpose: compare candidate NLU parsing with the production parser. Why: side-by-side results make model changes safer to evaluate. */
function HybridTestConsole({ botId, environmentId }) {
  const [text, setText] = useState(''); const [result, setResult] = useState(null); const [comparison, setComparison] = useState(null); const [running, setRunning] = useState(false); const [error, setError] = useState(''); const [models, setModels] = useState([]); const [version, setVersion] = useState('production');
  useEffect(() => { axios.get('/api/v1/nlu/models', { params: { botId, environment: environmentId } }).then(({ data }) => setModels((data.models || []).filter((model) => ['ready', 'published', 'archived'].includes(model.status)))).catch(() => setModels([])); }, [botId, environmentId]);
  // Parse against the selected model; candidate tests also capture production output for comparison.
  /** Purpose: Handle run in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const run = async () => { setRunning(true); setError(''); setComparison(null); try { const candidate = version !== 'production'; const payload = { botId, environment: environmentId, channel: 'web', text, debug: true }; if (candidate) { const [candidateResponse, productionResponse] = await Promise.allSettled([axios.post('/api/v1/nlu/parse-candidate', { ...payload, version: Number(version) }), axios.post('/api/v1/nlu/parse', payload)]); if (candidateResponse.status === 'rejected') throw candidateResponse.reason; setResult(candidateResponse.value.data); if (productionResponse.status === 'fulfilled') setComparison(productionResponse.value.data); } else { const { data } = await axios.post('/api/v1/nlu/parse', payload); setResult(data); } } catch (e) { setError(e.response?.data?.error || 'NLU parse failed'); } finally { setRunning(false); } };
  return <Paper sx={(theme) => ({ ...getCardSx(theme), p: 3 })}>
    <Typography variant="h6" sx={{ fontWeight: 800 }}>Hybrid NLU Test Console</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: .5, mb: 2 }}>Test Production or a Ready candidate without changing the active model.</Typography>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}><TextField select sx={{ minWidth: 190 }} label="Model" value={version} onChange={(e) => { setVersion(e.target.value); setResult(null); }}><MenuItem value="production">Production</MenuItem>{models.filter((model) => !model.isActive).map((model) => <MenuItem key={model.version} value={String(model.version)}>Candidate v{model.version} · {model.status}</MenuItem>)}</TextField><TextField fullWidth label="User message" value={text} onChange={(e) => setText(e.target.value)} placeholder="mera claim kaha tak pahucha" /><Button variant="contained" onClick={run} disabled={!text.trim() || running}>{running ? <CircularProgress size={18} /> : 'Test NLU'}</Button></Stack>
    {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
    {result ? <Stack spacing={2} sx={{ mt: 3 }}><Stack direction="row" spacing={2} alignItems="center"><DecisionChip status={result.status} /><Typography variant="h6">{result.intent?.name || result.topIntent?.name || 'No intent'}</Typography><Chip label={`${Math.round((result.intent?.confidence || result.confidence || 0) * 100)}%`} /><Chip variant="outlined" label={result.language || 'unknown'} /><Chip variant="outlined" label={`v${result.modelVersion}`} /></Stack>
      {comparison ? <Alert severity={(comparison.intent?.id || comparison.topIntent?.id) === (result.intent?.id || result.topIntent?.id) ? 'success' : 'warning'}>Production v{comparison.modelVersion}: {comparison.intent?.name || comparison.topIntent?.name || comparison.status} ({Math.round((comparison.intent?.confidence || comparison.confidence || 0) * 100)}%) · Candidate v{result.modelVersion}: {result.intent?.name || result.topIntent?.name || result.status} ({Math.round((result.intent?.confidence || result.confidence || 0) * 100)}%)</Alert> : null}
      <Grid container spacing={2}>{Object.entries(result.debug || {}).filter(([,v]) => typeof v === 'number').map(([key,value]) => <Grid item xs={6} md={3} key={key}><Paper variant="outlined" sx={{ p: 1.5 }}><Typography variant="caption" color="text.secondary">{key}</Typography><Typography sx={{ fontWeight: 800 }}>{Number(value).toFixed(3)}</Typography></Paper></Grid>)}</Grid>
      <Typography variant="subtitle2">Top candidates</Typography>{[result.topIntent, ...(result.alternatives || [])].filter(Boolean).map((item) => <Stack key={item.id || item.name} direction="row" justifyContent="space-between"><Typography>{item.name}</Typography><Typography>{Number(item.confidence || 0).toFixed(3)}</Typography></Stack>)}
    </Stack> : null}
  </Paper>;
}

/** Purpose: show asynchronous training job progress. Why: candidate models move through multiple stages before becoming testable. */
function TrainingPanel({ botId, environmentId, initialJob, onTrain, training }) {
  const [job, setJob] = useState(initialJob); const [model, setModel] = useState(null);
  useEffect(() => { setJob(initialJob); }, [initialJob]);
  // Poll only while a job is active, and clear the timer when the job or page changes.
  useEffect(() => { if (!job?.trainingJobId) return undefined; let timer; /** Purpose: Load poll for the train module. Why: The selected view needs current data to render or continue processing. */ const poll = async () => { try { const { data } = await axios.get(`/api/v1/nlu/training-jobs/${job.trainingJobId}`, { params: { botId, environment: environmentId } }); setModel(data.model); if (!['ready','failed'].includes(data.model.status)) timer = setTimeout(poll, 2000); } catch (_) {} }; poll(); return () => clearTimeout(timer); }, [job, botId, environmentId]);
  const stages = model?.stages || {};
  return <Paper sx={(theme) => ({ ...getCardSx(theme), p: 3 })}><Stack direction="row" justifyContent="space-between"><Box><Typography variant="h6" sx={{ fontWeight: 800 }}>Model training</Typography><Typography color="text.secondary">Creates an immutable candidate. It will not affect production until published.</Typography></Box><Button variant="contained" onClick={onTrain} disabled={training}>{training ? <CircularProgress size={18} /> : 'Train Model'}</Button></Stack>
    {model ? <Stack spacing={1.5} sx={{ mt: 3 }}><Stack direction="row" spacing={2}><Chip label={`v${model.version}`} /><DecisionChip status={model.status} /></Stack>{['validation','embeddings','evaluation','ready'].map((stage) => <Typography key={stage}>{stages[stage] === 'complete' ? '✓' : stages[stage] === 'running' ? '●' : stages[stage] === 'failed' ? '✕' : '○'} {stage}</Typography>)}{model.metrics ? <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">{Object.entries(model.metrics).map(([k,v]) => <Chip key={k} variant="outlined" label={`${k}: ${v == null ? 'n/a' : `${(v*100).toFixed(1)}%`}`} />)}</Stack> : null}{model.error ? <Alert severity="error">{model.error}</Alert> : null}</Stack> : <Alert severity="info" sx={{ mt: 3 }}>No training job selected.</Alert>}
  </Paper>;
}

/** Purpose: list model versions and promotion/rollback actions. Why: operators need a traceable way to manage deployed NLU models. */
function ModelVersionsPanel({ botId, environmentId }) {
  const [models, setModels] = useState([]); const [error, setError] = useState('');
  /** Purpose: Load load for the train module. Why: The selected view needs current data to render or continue processing. */
  const load = useCallback(async () => { try { const { data } = await axios.get('/api/v1/nlu/models', { params: { botId, environment: environmentId } }); setModels(data.models || []); } catch (e) { setError(e.response?.data?.error || 'Failed to load models'); } }, [botId, environmentId]);
  useEffect(() => { load(); }, [load]);
  // Promote or roll back a model version, then reload server-authoritative version state.
  /** Purpose: Transform activate in the train module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
  const activate = async (version, rollback = false) => { setError(''); try { await axios.post(`/api/v1/nlu/models/${rollback ? 'rollback' : 'publish'}`, { botId, environment: environmentId, version }); load(); } catch (e) { const details = e.response?.data?.details?.issues || []; setError(`${e.response?.data?.error || e.response?.data?.message || 'Model activation failed'}${details.length ? `: ${details.map((item) => `${item.metric} ${item.reason}`).join(', ')}` : ''}`); } };
  return <Stack spacing={2}>{error ? <Alert severity="error">{error}</Alert> : null}{models.map((model, index) => <Paper key={model.version} sx={(theme) => ({ ...getCardSx(theme), p: 2.5 })}><Stack direction={{xs:'column',md:'row'}} justifyContent="space-between" spacing={2}><Stack direction="row" spacing={1.5} alignItems="center"><Typography variant="h6" sx={{fontWeight:800}}>v{model.version}</Typography><Chip color={model.isActive ? 'success' : 'default'} label={model.isActive ? 'PRODUCTION' : model.status.toUpperCase()} /></Stack><Stack direction="row" spacing={1}>{model.status === 'ready' ? <Button variant="contained" onClick={() => activate(model.version)}>Publish</Button> : null}{!model.isActive && ['ready','archived','published'].includes(model.status) ? <Button onClick={() => activate(model.version, true)}>Rollback</Button> : null}</Stack></Stack></Paper>)}</Stack>;
}

/** Purpose: load and display NLU quality analytics for an environment. Why: release decisions should be informed by measured outcomes. */
function NluAnalyticsPanel({ botId, environmentId }) {
  const [data, setData] = useState(null); useEffect(() => { axios.get('/api/v1/nlu/analytics', { params: { botId, environment: environmentId } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [botId, environmentId]);
  if (!data) return <CircularProgress />;
  return <Stack spacing={3}><Grid container spacing={2}>{[['Messages',data.totalMessages],['Recognized',data.recognizedRate],['Fallback',data.fallbackRate],['Ambiguous',data.ambiguousRate],['OOS',data.outOfScopeRate],['Correction',data.correctionRate]].map(([label,value],i) => <Grid item xs={6} md={2} key={label}><StatsCard label={label} value={i ? `${((value || 0)*100).toFixed(1)}%` : value} helper="Published model events" /></Grid>)}</Grid><Grid container spacing={2}><Grid item xs={12} md={4}><AnalyticsBreakdown title="Channels" rows={(data.channelDistribution || []).map((item) => ({ label: item.channel, value: item.count }))} /></Grid><Grid item xs={12} md={4}><AnalyticsBreakdown title="Intent distribution" rows={(data.intentDistribution || []).map((item) => ({ label: item.intentId, value: item.count }))} /></Grid><Grid item xs={12} md={4}><AnalyticsBreakdown title="Model decisions" rows={(data.modelPerformance || []).map((item) => ({ label: `v${item.modelVersion || '?'} · ${item.status}`, value: item.count }))} /></Grid></Grid><NluReviewQueue botId={botId} environmentId={environmentId} /></Stack>;
}

/** Purpose: render one grouped analytics breakdown. Why: repeated metric groups should share a predictable presentation. */
function AnalyticsBreakdown({ title, rows }) {
  return <Paper sx={(theme) => ({ ...getCardSx(theme), p: 2.5, height: '100%' })}><Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>{title}</Typography>{!rows.length ? <Typography variant="body2" color="text.secondary">No data yet.</Typography> : <Stack spacing={1}>{rows.slice(0, 8).map((row) => <Stack key={row.label} direction="row" justifyContent="space-between" spacing={2}><Typography variant="body2" noWrap title={row.label}>{row.label}</Typography><Chip size="small" label={row.value} /></Stack>)}</Stack>}</Paper>;
}

/** Purpose: review messages that need intent labeling or dismissal. Why: human feedback can improve training data from real traffic. */
function NluReviewQueue({ botId, environmentId }) {
  const [events, setEvents] = useState([]); const [intents, setIntents] = useState([]); const [choices, setChoices] = useState({}); const [error, setError] = useState('');
  /** Purpose: Load load for the train module. Why: The selected view needs current data to render or continue processing. */
  const load = useCallback(async () => { try { const [review, intentResponse] = await Promise.all([axios.get('/api/v1/nlu/review', { params: { botId, environment: environmentId, status: 'pending' } }), axios.get('/api/v1/train/intents', { params: { botId, environmentId } })]); setEvents(review.data.events || []); setIntents(intentResponse.data.intents || []); } catch (e) { setError(e.response?.data?.error || 'Review queue could not be loaded'); } }, [botId, environmentId]);
  useEffect(() => { load(); }, [load]);
  /** Purpose: Apply update in the train module. Why: Centralizing this change keeps the relevant state or persisted record consistent. */
  const update = async (event, action) => { try { if (action === 'add') { const intentId = choices[event._id]; if (!intentId) return; await axios.post(`/api/v1/nlu/review/${event._id}/add-to-intent`, { botId, environment: environmentId, intentId }); } else await axios.post(`/api/v1/nlu/review/${event._id}/ignore`, { botId, environment: environmentId }); setEvents((current) => current.filter((item) => item._id !== event._id)); } catch (e) { setError(e.response?.data?.error || 'Review action failed'); } };
  return <Paper sx={(theme) => ({ ...getCardSx(theme), p: 2.5 })}><Typography variant="h6" sx={{ fontWeight: 800 }}>Human review queue</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Corrections update draft phrases only. Train and Publish are still required.</Typography>{error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}{!events.length ? <Alert severity="success">No pending low-confidence messages.</Alert> : <Stack spacing={1.5}>{events.map((event) => <Paper variant="outlined" key={event._id} sx={{ p: 2 }}><Typography sx={{ fontWeight: 700 }}>&quot;{event.redactedText || 'Redacted message'}&quot;</Typography><Typography variant="caption" color="text.secondary">{event.status} · {Math.round((event.confidence || 0) * 100)}% · detected {event.topIntentName || 'none'}</Typography><Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1.5 }}><TextField select size="small" label="Correct intent" value={choices[event._id] || ''} onChange={(e) => setChoices((current) => ({ ...current, [event._id]: e.target.value }))} sx={{ minWidth: 240 }}>{intents.map((intent) => <MenuItem key={intent.intentId} value={intent.intentId}>{intent.name}</MenuItem>)}</TextField><Button variant="contained" disabled={!choices[event._id]} onClick={() => update(event, 'add')}>Add to intent</Button><Button color="inherit" onClick={() => update(event, 'ignore')}>Ignore</Button></Stack></Paper>)}</Stack>}</Paper>;
}

/** Purpose: manage intents and their phrases/responses. Why: intent definitions are the primary training units for NLU classification. */
function IntentsTab({ botId, environmentId, onRefreshOverview }) {
  const [items, setItems] = useState([]);
  const [entities, setEntities] = useState([]);
  const [flows, setFlows] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const selectedIntentIdRef = useRef('');

  /** Purpose: Handle refresh suggestions in the train module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const refreshSuggestions = useCallback(async (draft) => {
    if (!draft?.name?.trim()) {
      setSelected((prev) => (prev ? { ...prev, suggestedPhrases: [] } : prev));
      return;
    }
    try {
      const response = await axios.post('/api/v1/train/intents/suggestions', {
        botId,
        environmentId,
        name: draft.name,
        entities: draft.entities || [],
      });
      setSelected((prev) => (prev ? { ...prev, suggestedPhrases: response.data.suggestions || [] } : prev));
    } catch (error) {
      // keep editor responsive even if suggestions fail
    }
  }, [botId, environmentId]);

  /** Purpose: Load load for the train module. Why: The selected view needs current data to render or continue processing. */
  const load = useCallback(async (selectedId = selectedIntentIdRef.current) => {
    if (!botId || !environmentId) return;
    try {
      setError('');
      const [intentResponse, entityResponse, flowResponse] = await Promise.all([
        axios.get('/api/v1/train/intents', { params: { botId, environmentId } }),
        axios.get('/api/v1/train/entities', { params: { botId, environmentId } }),
        axios.get('/api/v1/flows', { params: { botId, environmentId } }),
      ]);
      const nextItems = intentResponse.data.intents || [];
      setItems(nextItems);
      setEntities(entityResponse.data.entities || []);
      setFlows(flowResponse.data.flows || []);
      const preferred = selectedId || selectedIntentIdRef.current;
      const match = nextItems.find((item) => item.intentId === preferred) || nextItems[0] || null;
      selectedIntentIdRef.current = match?.intentId || '';
      setSelected(match ? toIntentDraft(match) : null);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Failed to load intents.');
    }
  }, [botId, environmentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selected) return;
    /** Purpose: Resolve timer for the train module. Why: The surrounding operation uses this value to make its decision. */
    const timer = setTimeout(() => {
      refreshSuggestions(selected);
    }, 250);
    return () => clearTimeout(timer);
  }, [selected?.name, selected?.entities, refreshSuggestions]);

  // Save a new or edited intent, then reload the selected record and overview counts.
  /** Purpose: Handle handle save in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handleSave = async (draft) => {
    setSaving(true);
    const payload = fromIntentDraft(draft, botId, environmentId);
    try {
      setError('');
      if (draft.intentId) {
        await axios.put(`/api/v1/train/intents/${draft.intentId}`, payload);
      } else {
        const response = await axios.post('/api/v1/train/intents', payload);
        draft = toIntentDraft(response.data.intent);
      }
      await load(draft.intentId);
      onRefreshOverview();
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Failed to save intent.');
    } finally {
      setSaving(false);
    }
  };

  // Remove the selected intent and refresh the list/overview after the API confirms deletion.
  /** Purpose: Handle handle delete in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handleDelete = async (draft) => {
    if (!draft.intentId) return;
    setSaving(true);
    try {
      await axios.delete(`/api/v1/train/intents/${draft.intentId}`);
      await load();
      onRefreshOverview();
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || 'Failed to delete intent.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SplitLayout
      title="Intents"
      subtitle="Match user goals using training phrases, linked entities, and canned responses."
      searchValue={search}
      onSearchChange={setSearch}
      list={items}
      selectedId={selected?.intentId}
      onSelect={(item) => {
        selectedIntentIdRef.current = item.intentId || '';
        setSelected(toIntentDraft(item));
      }}
      renderLabel={(item) => item.name}
      renderMeta={(item) => `${item.userUtterances?.length || 0} phrases • ${(item.responses || []).length} responses`}
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            selectedIntentIdRef.current = '';
            setSelected(toIntentDraft());
          }}
        >
          New
        </Button>
      }
      error={error}
      detail={
        selected ? (
          <IntentEditor
            entityOptions={entities}
            flowOptions={flows}
            value={selected}
            onChange={(key, val) => setSelected((prev) => ({ ...prev, [key]: val }))}
            onSave={handleSave}
            onDelete={handleDelete}
            saving={saving}
          />
        ) : (
          <EmptyDetail title="No intent selected" subtitle="Pick an intent from the left or create a fresh one." />
        )
      }
    />
  );
}

/** Purpose: provide shared list/edit/create/delete behavior for NLU resources. Why: each tab can reuse consistent CRUD behavior while supplying its own mapping and fields. */
function CrudTab({ endpoint, botId, environmentId, onRefreshOverview, toDraft, fromDraft, renderTitle, renderSubtitle, renderMeta, fields, emptyTitle, emptySubtitle }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const selectedItemIdRef = useRef('');

  /** Purpose: Load load for the train module. Why: The selected view needs current data to render or continue processing. */
  const load = useCallback(async (selectedId = selectedItemIdRef.current) => {
    if (!botId || !environmentId) return;
    try {
      setError('');
      const response = await axios.get(`/api/v1/train/${endpoint}`, { params: { botId, environmentId } });
      const key = endpoint === 'smalltalk' ? 'smallTalks' : endpoint;
      const nextItems = response.data[key] || response.data.items || [];
      setItems(nextItems);
      const preferred = selectedId || selectedItemIdRef.current;
      const match = nextItems.find((item) => toDraft(item).id === preferred) || nextItems[0] || null;
      selectedItemIdRef.current = match ? toDraft(match).id : '';
      setSelected(match ? toDraft(match) : null);
    } catch (loadError) {
      setError(loadError.response?.data?.error || `Failed to load ${renderTitle.toLowerCase()}.`);
    }
  }, [botId, endpoint, environmentId, toDraft]);

  useEffect(() => {
    load();
  }, [load]);

  // Convert the generic resource draft to its endpoint-specific API payload and refresh the list.
  /** Purpose: Handle handle save in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handleSave = async (draft) => {
    setSaving(true);
    const payload = fromDraft(draft, botId, environmentId);
    try {
      setError('');
      if (draft.id) {
        await axios.put(`/api/v1/train/${endpoint}/${draft.id}`, payload);
      } else {
        const response = await axios.post(`/api/v1/train/${endpoint}`, payload);
        const created =
          response.data.entity ||
          response.data.faq ||
          response.data.synonym ||
          response.data.smallTalk ||
          response.data.context ||
          response.data.item;
        draft = toDraft(created);
      }
      await load(draft.id);
      onRefreshOverview();
    } catch (saveError) {
      setError(saveError.response?.data?.error || `Failed to save ${renderTitle.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  // Delete the selected generic resource and refresh the list to keep editor state in sync.
  /** Purpose: Handle handle delete in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const handleDelete = async (draft) => {
    if (!draft.id) return;
    setSaving(true);
    try {
      await axios.delete(`/api/v1/train/${endpoint}/${draft.id}`);
      await load();
      onRefreshOverview();
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || `Failed to delete ${renderTitle.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SplitLayout
      title={renderTitle}
      subtitle={renderSubtitle}
      searchValue={search}
      onSearchChange={setSearch}
      list={items}
      selectedId={selected?.id}
      onSelect={(item) => {
        const nextDraft = toDraft(item);
        selectedItemIdRef.current = nextDraft.id || '';
        setSelected(nextDraft);
      }}
      renderLabel={(item) => toDraft(item).name}
      renderMeta={renderMeta}
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            selectedItemIdRef.current = '';
            setSelected(toDraft());
          }}
        >
          New
        </Button>
      }
      error={error}
      detail={
        selected ? (
          <GenericEditor
            title={selected.name || renderTitle}
            subtitle={renderSubtitle}
            value={selected}
            onChange={(key, value) => setSelected((prev) => ({ ...prev, [key]: value }))}
            onSave={handleSave}
            onDelete={handleDelete}
            saving={saving}
            fields={fields}
          />
        ) : (
          <EmptyDetail title={emptyTitle} subtitle={emptySubtitle} />
        )
      }
    />
  );
}

/** Purpose: configure entity types and extraction patterns. Why: entities capture structured values from user messages. */
function EntitiesTab(props) {
  const { botId, environmentId } = props;
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // Send the current sample to the entity extraction endpoint and expose the returned matches.
  /** Purpose: Handle run extract in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const runExtract = async () => {
    if (!testInput.trim()) return;
    setTesting(true);
    try {
      const response = await axios.post('/api/v1/train/entities/extract', {
        botId,
        environmentId,
        message: testInput.trim(),
      });
      setTestResult(response.data);
    } catch (error) {
      // surface the error in the same panel by reusing the empty result state
      setTestResult({ entities: [], normalizedMessage: '', error: error.response?.data?.error || 'Entity extraction failed.' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box>
      <EntityTestPanel input={testInput} onChange={setTestInput} onRun={runExtract} running={testing} result={testResult} />
      <CrudTab
        {...props}
        endpoint="entities"
        renderTitle="Entities"
        renderSubtitle="Capture structured values and map synonyms to canonical values."
        renderMeta={(item) => `${item.values?.length || 0} values • ${item.type || 'custom'}`}
        emptyTitle="No entity selected"
        emptySubtitle="Choose an entity from the list or create a new one."
        toDraft={(item) => ({
          id: item?.entityId || '',
          name: item?.name || '',
          description: item?.description || '',
          type: item?.type || 'custom',
          valuesText: joinLines((item?.values || []).map((entry) => `${entry.value}:${(entry.synonyms || []).join(', ')}`)),
        })}
        fromDraft={(draft, botId, environmentId) => ({
          botId,
          environmentId,
          name: draft.name,
          description: draft.description,
          type: draft.type,
          values: splitLines(draft.valuesText).map((line) => {
            const [value, synonyms] = line.split(':');
            return {
              value: (value || '').trim(),
              synonyms: String(synonyms || '')
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean),
            };
          }),
        })}
        fields={[
          { key: 'name', label: 'Entity name', md: 6 },
          { key: 'type', label: 'Entity type', md: 6, options: [{ value: 'custom', label: 'Custom' }, { value: 'system', label: 'System' }, { value: 'regex', label: 'Regex' }] },
          { key: 'description', label: 'Description' },
          { key: 'valuesText', label: 'Values and synonyms', helperText: 'One per line: Delhi: delhi, dilli', multiline: true, minRows: 10 },
        ]}
      />
    </Box>
  );
}

/** Purpose: manage FAQ question/answer records. Why: FAQ matching provides an explicit answer path for common questions. */
function FaqsTab(props) {
  return (
    <CrudTab
      {...props}
      endpoint="faqs"
      renderTitle="FAQs"
      renderSubtitle="Use FAQs as a smart fallback when no strong intent is matched."
      renderMeta={(item) => `${item.tags?.length || 0} tags • priority ${item.priority || 0}`}
      emptyTitle="No FAQ selected"
      emptySubtitle="Choose an FAQ or create one to improve fallback coverage."
      toDraft={(item) => ({
        id: item?.faqId || '',
        name: item?.question || '',
        answer: item?.answer || '',
        category: item?.category || 'general',
        tagsText: joinLines(item?.tags || []),
        priority: item?.priority || 0,
      })}
      fromDraft={(draft, botId, environmentId) => ({
        botId,
        environmentId,
        question: draft.name,
        answer: draft.answer,
        category: draft.category,
        tags: splitLines(draft.tagsText),
        priority: Number(draft.priority) || 0,
      })}
      fields={[
        { key: 'name', label: 'Question' },
        { key: 'answer', label: 'Answer', multiline: true, minRows: 5 },
        { key: 'category', label: 'Category', md: 6 },
        { key: 'priority', label: 'Priority', md: 6 },
        { key: 'tagsText', label: 'Tags', helperText: 'One tag per line', multiline: true, minRows: 5 },
      ]}
    />
  );
}

/** Purpose: manage equivalent terms used during matching. Why: varied wording can map to the same intent/entity value. */
function SynonymsTab(props) {
  return (
    <CrudTab
      {...props}
      endpoint="synonyms"
      renderTitle="Synonyms"
      renderSubtitle="Normalize surface language before intent detection."
      renderMeta={(item) => `${item.synonyms?.length || 0} variants`}
      emptyTitle="No synonym group selected"
      emptySubtitle="Create canonical terms like yes / no / booking to improve matching."
      toDraft={(item) => ({
        id: item?.synonymId || '',
        name: item?.word || '',
        synonymsText: joinLines(item?.synonyms || []),
      })}
      fromDraft={(draft, botId, environmentId) => ({
        botId,
        environmentId,
        word: draft.name,
        synonyms: splitLines(draft.synonymsText),
      })}
      fields={[
        { key: 'name', label: 'Canonical word' },
        { key: 'synonymsText', label: 'Synonyms', helperText: 'One variant per line', multiline: true, minRows: 8 },
      ]}
    />
  );
}

/** Purpose: manage conversational small-talk examples and replies. Why: greetings and casual exchanges should not require business intents. */
function SmallTalkTab(props) {
  return (
    <CrudTab
      {...props}
      endpoint="smalltalk"
      renderTitle="Small Talk"
      renderSubtitle="Handle greeting, thanks, goodbye, and polite chit-chat without triggering flows."
      renderMeta={(item) => `${item.category || 'other'} • ${item.botResponse?.slice(0, 48) || ''}`}
      emptyTitle="No small talk entry selected"
      emptySubtitle="Add common conversational responses for a more human assistant."
      toDraft={(item) => ({
        id: item?.smallTalkId || '',
        name: item?.userInput || '',
        category: item?.category || 'greeting',
        response: item?.botResponse || '',
      })}
      fromDraft={(draft, botId, environmentId) => ({
        botId,
        environmentId,
        category: draft.category,
        userInput: draft.name,
        botResponse: draft.response,
      })}
      fields={[
        { key: 'category', label: 'Category', md: 6, options: [{ value: 'greeting', label: 'Greeting' }, { value: 'goodbye', label: 'Goodbye' }, { value: 'thanks', label: 'Thanks' }, { value: 'apology', label: 'Apology' }, { value: 'compliment', label: 'Compliment' }, { value: 'other', label: 'Other' }] },
        { key: 'name', label: 'User input', md: 6 },
        { key: 'response', label: 'Bot response', multiline: true, minRows: 6 },
      ]}
    />
  );
}

/** Purpose: manage context/session rules for NLU. Why: multi-turn conversations need state-aware interpretation beyond isolated messages. */
function ContextTab({ botId, environmentId, onRefreshOverview }) {
  const [configs, setConfigs] = useState([]);
  const [liveContexts, setLiveContexts] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const selectedContextIdRef = useRef('');

  /** Purpose: Load load for the train module. Why: The selected view needs current data to render or continue processing. */
  const load = useCallback(async (selectedId = selectedContextIdRef.current) => {
    const [configResponse, liveResponse] = await Promise.all([
      axios.get('/api/v1/train/context', { params: { botId, environmentId } }),
      axios.get('/api/v1/train/context/live', { params: { botId, environmentId, limit: 20 } }),
    ]);
    const nextConfigs = configResponse.data.contexts || [];
    setConfigs(nextConfigs);
    setLiveContexts(liveResponse.data.contexts || []);
    const match = nextConfigs.find((item) => item.contextId === (selectedId || selectedContextIdRef.current)) || nextConfigs[0] || null;
    selectedContextIdRef.current = match?.contextId || '';
    setSelected(match ? toContextDraft(match) : null);
  }, [botId, environmentId]);

  useEffect(() => {
    load();
  }, [load]);

  // Save a context rule set, then reload its persisted version and related overview data.
  /** Purpose: Handle save in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const save = async (draft) => {
    setSaving(true);
    const payload = fromContextDraft(draft, botId, environmentId);
    try {
      if (draft.id) {
        await axios.put(`/api/v1/train/context/${draft.id}`, payload);
      } else {
        const response = await axios.post('/api/v1/train/context', payload);
        draft = toContextDraft(response.data.context);
      }
      await load(draft.id);
      onRefreshOverview();
    } finally {
      setSaving(false);
    }
  };

  // Remove a context rule set and clear selection if it was the active editor record.
  /** Purpose: Handle remove in the train module. Why: This keeps the corresponding action and its state or request update consistent. */
  const remove = async (draft) => {
    if (!draft.id) return;
    setSaving(true);
    try {
      await axios.delete(`/api/v1/train/context/${draft.id}`);
      await load();
      onRefreshOverview();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} md={4}>
        <Paper sx={(theme) => ({ ...getCardSx(theme), p: 2.5, mb: 2.5 })}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Context rules</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Define slot-filling and session memory defaults.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                selectedContextIdRef.current = '';
                setSelected(toContextDraft());
              }}
            >
              New
            </Button>
          </Stack>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search contexts"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={(theme) => ({ ...getTextFieldSx(theme), mb: 2 })}
          />
          <List sx={{ p: 0 }}>
            {configs
              .filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
              .map((item) => (
                <ListItemButton
                  key={item.contextId}
                  selected={selected?.id === item.contextId}
                  onClick={() => {
                    selectedContextIdRef.current = item.contextId || '';
                    setSelected(toContextDraft(item));
                  }}
                  sx={{ borderRadius: 2.5, mb: 1, border: '1px solid', borderColor: 'divider' }}
                >
                  <ListItemText primary={item.name} secondary={`${item.variables?.length || 0} variables • ${item.rules?.length || 0} rules`} />
                </ListItemButton>
              ))}
          </List>
        </Paper>

        <Paper sx={(theme) => ({ ...getCardSx(theme), p: 2.5 })}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Live contexts</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 2 }}>
            Recent runtime sessions with tracked intent and entities.
          </Typography>
          <Stack spacing={1.25}>
            {liveContexts.map((context) => (
              <Paper key={context.sessionKey} variant="outlined" sx={(theme) => ({ ...getMutedPanelSx(theme), p: 1.5 })}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {context.lastIntent || 'No intent yet'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                  {context.channel} • {context.turnCount || 0} turns
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {(context.entities || []).slice(0, 3).map((entity) => (
                    <Chip key={`${context.sessionKey}-${entity.name}`} size="small" label={`${entity.name}: ${entity.value}`} />
                  ))}
                </Stack>
              </Paper>
            ))}
            {liveContexts.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No active contexts yet.</Typography>
            ) : null}
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        {selected ? (
          <GenericEditor
            title={selected.name || 'Context configuration'}
            subtitle="Use variables and rules to guide multi-turn memory and slot filling."
            value={selected}
            onChange={(key, value) => setSelected((prev) => ({ ...prev, [key]: value }))}
            onSave={save}
            onDelete={remove}
            saving={saving}
            fields={[
              { key: 'name', label: 'Context name', md: 6 },
              { key: 'description', label: 'Description', md: 6 },
              { key: 'variablesText', label: 'Variables', helperText: 'One per line: city|string|Delhi', multiline: true, minRows: 8 },
              { key: 'slotPromptsText', label: 'Slot prompts', helperText: 'One per line: city|Which city are you flying to?|true', multiline: true, minRows: 8 },
              { key: 'rulesText', label: 'Rules', helperText: 'One per line: missing_city -> ask_city -> city', multiline: true, minRows: 8 },
            ]}
          />
        ) : (
          <EmptyDetail title="No context selected" subtitle="Select a context rule set or create a new one." />
        )}
      </Grid>
    </Grid>
  );
}

/** Purpose: map an API intent into editable form values. Why: the editor uses multiline text while the API stores phrase arrays. */
function toIntentDraft(item = {}) {
  const flows = Array.isArray(item.flowsConnected)
    ? item.flowsConnected.filter(Boolean)
    : (item.flowsConnected ? [item.flowsConnected] : []);
  return {
    intentId: item.intentId || '',
    name: item.name || '',
    description: item.description || '',
    trainingPhrasesText: joinLines((item.userUtterances || []).map((entry) => entry.text).filter(Boolean)),
    responsesText: joinLines(item.responses || []),
    entities: item.entities || [],
    flowsConnected: flows.slice(0, 1),
    suggestedPhrases: item.trainingMeta?.suggestedPhrases || [],
  };
}

/** Purpose: map intent form values back to the API contract. Why: centralizes normalization before create/update requests. */
function fromIntentDraft(draft, botId, environmentId) {
  const flows = Array.isArray(draft.flowsConnected)
    ? draft.flowsConnected.filter(Boolean)
    : (draft.flowsConnected ? [draft.flowsConnected] : []);
  return {
    botId,
    environmentId,
    name: draft.name,
    description: draft.description,
    trainingPhrases: splitLines(draft.trainingPhrasesText),
    responses: splitLines(draft.responsesText),
    entities: draft.entities || [],
    flowsConnected: flows.slice(0, 1),
  };
}

/** Purpose: map a stored context rule into editor state. Why: keeps the form independent of optional API fields. */
function toContextDraft(item = {}) {
  return {
    id: item.contextId || '',
    name: item.name || '',
    description: item.description || '',
    variablesText: joinLines((item.variables || []).map((entry) => `${entry.name}|${entry.type || 'string'}|${entry.defaultValue ?? ''}`)),
    slotPromptsText: joinLines((item.slotPrompts || []).map((entry) => `${entry.entity || ''}|${entry.prompt || ''}|${entry.required !== false}`)),
    rulesText: joinLines((item.rules || []).map((entry) => `${entry.condition || ''} -> ${entry.action || ''} -> ${entry.target || ''}`)),
  };
}

/** Purpose: map context editor state to the API contract. Why: ensures create/update requests carry normalized scope and metadata. */
function fromContextDraft(draft, botId, environmentId) {
  return {
    botId,
    environmentId,
    name: draft.name,
    description: draft.description,
    variables: splitLines(draft.variablesText).map((line) => {
      const [name, type, defaultValue] = line.split('|');
      return { name: (name || '').trim(), type: (type || 'string').trim(), defaultValue: (defaultValue || '').trim() };
    }),
    slotPrompts: splitLines(draft.slotPromptsText).map((line) => {
      const [entity, prompt, required] = line.split('|');
      return {
        entity: (entity || '').trim(),
        prompt: (prompt || '').trim(),
        required: String(required || 'true').trim().toLowerCase() !== 'false',
      };
    }),
    rules: splitLines(draft.rulesText).map((line) => {
      const [condition, action, target] = line.split('->');
      return {
        condition: (condition || '').trim(),
        action: (action || '').trim(),
        target: (target || '').trim(),
      };
    }),
  };
}
